import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout, { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Lightning, Receipt, ShieldCheck, Sparkle, IdentificationCard, DownloadSimple } from "@phosphor-icons/react";

function detectCurrency() {
  const saved = localStorage.getItem("billing_currency");
  if (saved === "INR" || saved === "USD") return saved;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const lang = (navigator.language || "").toLowerCase();
    if (tz === "Asia/Kolkata" || lang.endsWith("-in")) return "INR";
  } catch (e) { /* ignore */ }
  return "USD";
}

function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load Razorpay Checkout"));
    document.body.appendChild(s);
  });
}

const fmt = (sym, n) => `${sym}${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const FEATURES = [
  "Unlimited migration projects & background jobs",
  "100+ connectors (Salesforce, HubSpot, SAP, SQL, Zendesk…)",
  "Update / Upsert with idempotent re-runs",
  "Team roles, audit logs & tenant isolation",
];

export default function Billing() {
  const { user } = useAuth();
  const [currency, setCurrency] = useState(detectCurrency());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payments, setPayments] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [profile, setProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => { api.get("/billing/profile").then((r) => setProfile(r.data)); }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await api.put("/billing/profile", profile); toast.success("Billing details saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const downloadInvoice = async (p) => {
    try {
      const res = await api.get(`/billing/invoice/${p.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${p.invoice_no || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download invoice"); }
  };

  const refresh = () => {
    api.get(`/billing/pricing?currency=${currency}`).then((r) => setData(r.data));
    api.get("/billing/payments").then((r) => setPayments(r.data));
  };
  useEffect(() => {
    let ignore = false;
    api.get(`/billing/pricing?currency=${currency}`).then((r) => { if (!ignore) setData(r.data); });
    api.get("/billing/payments").then((r) => { if (!ignore) setPayments(r.data); });
    return () => { ignore = true; };
  }, [currency]);

  const chooseCurrency = (c) => { setCurrency(c); localStorage.setItem("billing_currency", c); };

  const q = data?.quote;
  const sub = data?.subscription;

  const pay = async () => {
    setBusy(true);
    try {
      await loadRazorpay();
      const { data: order } = await api.post("/billing/order", { currency });
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Inspiro Interface",
        description: `${order.quote.plan_name} plan — monthly`,
        order_id: order.order_id,
        prefill: { email: user?.email, name: user?.name },
        theme: { color: "#C2410C" },
        handler: async (r) => {
          try {
            await api.post("/billing/verify", {
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            toast.success("Payment successful — subscription activated");
            refresh();
          } catch (e) { toast.error("Payment verification failed"); }
        },
        modal: { ondismiss: () => setBusy(false) },
      };
      const rzp = new window.Razorpay(opts);
      rzp.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed"));
      rzp.open();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((d && d.message) || apiError(d) || "Could not start payment");
    } finally { setBusy(false); }
  };

  const openInvoice = async (id) => {
    try { const { data } = await api.get(`/billing/invoice/${id}`); setInvoice({ ...data, _pid: id }); }
    catch (e) { toast.error("Invoice not available"); }
  };

  const paid = payments.filter((p) => p.status === "paid");

  return (
    <Layout>
      <PageHeader title="Billing & Subscription" subtitle="Manage your plan, currency and invoices" />
      <div className="p-8 max-w-5xl space-y-8">
        {/* Currency + subscription status bar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="inline-flex rounded-full bg-stone-100 p-1" data-testid="billing-currency-toggle">
            {["INR", "USD"].map((c) => (
              <button key={c} data-testid={`billing-currency-${c.toLowerCase()}`}
                onClick={() => chooseCurrency(c)}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${
                  currency === c ? "bg-[#C2410C] text-white shadow-sm" : "text-stone-600 hover:text-stone-900"}`}>
                {c === "INR" ? "₹ INR" : "$ USD"}
              </button>
            ))}
          </div>
          {sub && (
            <div data-testid="subscription-status" className="text-sm">
              {sub.is_free ? (
                <span className="inline-flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5">
                  <Sparkle size={15} weight="fill" /> Complimentary plan — no payment required
                </span>
              ) : sub.status === "active" ? (
                <span className="inline-flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-4 py-1.5">
                  <CheckCircle size={15} weight="fill" /> Active
                  {sub.current_period_end && <span className="text-stone-500 font-mono">· renews {new Date(sub.current_period_end).toLocaleDateString()}</span>}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5">
                  Inactive — subscribe to continue
                </span>
              )}
            </div>
          )}
        </div>

        {/* Plan card */}
        {q && (
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
            <div className="bg-white border border-stone-200 rounded-3xl shadow-card p-8">
              <div className="flex items-center gap-2 text-[#C2410C]">
                <Lightning size={20} weight="fill" />
                <span className="text-xs uppercase tracking-[0.18em] font-bold">{q.plan_name} Plan</span>
              </div>
              <div className="mt-4 flex items-end gap-2">
                <span data-testid="billing-plan-price" className="font-head text-5xl font-semibold text-stone-950">
                  {fmt(q.symbol, q.base)}
                </span>
                <span className="text-stone-500 mb-1.5 text-sm">/ company / month</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {FEATURES.map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-stone-700">
                    <CheckCircle size={18} weight="fill" className="text-[#C2410C] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              {currency === "USD" && (
                <p className="mt-6 text-xs text-stone-500">
                  Need custom USD pricing or an invoice?{" "}
                  <a data-testid="billing-sales-link" href={`mailto:${data.sales_email}`}
                    className="text-[#C2410C] font-medium underline">Contact {data.sales_email}</a>
                </p>
              )}
            </div>

            {/* Order summary */}
            <div className="bg-white border border-stone-200 rounded-3xl shadow-card p-8 flex flex-col">
              <h3 className="font-head font-semibold text-lg text-stone-950">Order summary</h3>
              <div className="mt-5 space-y-3 text-sm flex-1">
                <div className="flex justify-between text-stone-600">
                  <span>{q.plan_name} (monthly)</span>
                  <span className="font-mono text-stone-900">{fmt(q.symbol, q.base)}</span>
                </div>
                {q.gst_applicable && (
                  <div className="flex justify-between text-stone-600" data-testid="billing-gst-row">
                    <span>GST ({q.gst_percent}%)</span>
                    <span className="font-mono text-stone-900">{fmt(q.symbol, q.gst)}</span>
                  </div>
                )}
                {!q.gst_applicable && (
                  <div className="text-xs text-stone-400">No GST on international (USD) billing.</div>
                )}
                <div className="border-t border-stone-100 pt-3 flex justify-between items-center">
                  <span className="font-medium text-stone-900">Total due</span>
                  <span data-testid="billing-total" className="font-head text-2xl font-semibold text-stone-950">
                    {fmt(q.symbol, q.total)}
                  </span>
                </div>
              </div>
              {sub?.is_free ? (
                <Button disabled className="mt-6 rounded-full w-full">Complimentary — no payment</Button>
              ) : (
                <Button data-testid="billing-pay-btn" onClick={pay} disabled={busy}
                  className="mt-6 rounded-full w-full bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200 h-11">
                  {busy ? "Starting…" : `Pay ${fmt(q.symbol, q.total)}`}
                </Button>
              )}
              <p className="mt-3 text-[11px] text-stone-400 flex items-center gap-1.5 justify-center">
                <ShieldCheck size={13} /> Secured by Razorpay
              </p>
            </div>
          </div>
        )}

        {/* Billing details */}
        {profile && (
          <div className="bg-white border border-stone-200 rounded-3xl shadow-card p-6" data-testid="billing-profile">
            <div className="flex items-center gap-2 mb-1">
              <IdentificationCard size={18} className="text-stone-500" />
              <h3 className="font-head font-semibold text-stone-950">Billing details</h3>
            </div>
            <p className="text-xs text-stone-500 mb-5">Used on your invoices and the emailed PDF receipt. GST number appears on INR tax invoices.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["company_name", "Company / legal name", "Inspiro Works Pvt Ltd"],
                ["billing_email", "Billing email", "accounts@company.com"],
                ["gst_number", "GST number (India)", "29ABCDE1234F1Z5"],
                ["address_line1", "Address line 1", "12 MG Road"],
                ["address_line2", "Address line 2", "Suite 400"],
                ["city", "City", "Bengaluru"],
                ["state", "State", "Karnataka"],
                ["postal_code", "Postal code", "560001"],
                ["country", "Country", "India"],
              ].map(([key, lbl, ph]) => (
                <div key={key} className={key === "company_name" || key === "address_line1" || key === "address_line2" ? "md:col-span-2" : ""}>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">{lbl}</Label>
                  <Input data-testid={`billing-profile-${key.replace(/_/g, "-")}`}
                    value={profile[key] || ""} placeholder={ph}
                    onChange={(e) => setProfile({ ...profile, [key]: e.target.value })}
                    className="mt-1 rounded-xl" />
                </div>
              ))}
            </div>
            <Button data-testid="billing-profile-save" onClick={saveProfile} disabled={savingProfile}
              className="mt-5 rounded-full bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200">
              {savingProfile ? "Saving…" : "Save billing details"}
            </Button>
          </div>
        )}

        {/* Invoices */}
        <div className="bg-white border border-stone-200 rounded-3xl shadow-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Receipt size={18} className="text-stone-500" />
            <h3 className="font-head font-semibold text-stone-950">Invoices</h3>
          </div>
          {paid.length === 0 ? (
            <p className="text-sm text-stone-400">No invoices yet.</p>
          ) : (
            <div className="divide-y divide-stone-100">
              {paid.map((p) => (
                <div key={p.id} data-testid={`invoice-row-${p.id}`} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <div className="font-medium text-stone-900">{p.invoice_no}</div>
                    <div className="text-xs text-stone-500 font-mono">{p.paid_at ? new Date(p.paid_at).toLocaleString() : ""}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-stone-900">{fmt(p.currency === "INR" ? "₹" : "$", p.total)} {p.currency}</span>
                    <Button size="sm" variant="outline" className="rounded-full text-xs"
                      data-testid={`invoice-view-${p.id}`} onClick={() => openInvoice(p.id)}>View</Button>
                    <Button size="sm" className="rounded-full text-xs bg-[#C2410C] hover:bg-[#9A3412] gap-1.5"
                      data-testid={`invoice-download-${p.id}`} onClick={() => downloadInvoice(p)}>
                      <DownloadSimple size={14} /> PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Invoice dialog */}
      <Dialog open={!!invoice} onOpenChange={(v) => !v && setInvoice(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-head tracking-wide" data-testid="invoice-title">{invoice?.title}</DialogTitle></DialogHeader>
          {invoice && (
            <div className="space-y-4 text-sm">
              <div className="flex justify-between">
                <div>
                  <div className="text-xs text-stone-400 uppercase tracking-wider">Billed to</div>
                  <div className="font-medium text-stone-900">{invoice.bill_to?.name || invoice.org_name}</div>
                  {invoice.bill_to?.email && <div className="text-stone-500">{invoice.bill_to.email}</div>}
                  {invoice.bill_to?.address_line1 && <div className="text-stone-500">{invoice.bill_to.address_line1}</div>}
                  {invoice.bill_to?.gst_number && <div className="text-stone-500">GSTIN: {invoice.bill_to.gst_number}</div>}
                </div>
                <div className="text-right">
                  <div className="text-xs text-stone-400 uppercase tracking-wider">Invoice No.</div>
                  <div className="font-mono text-stone-900">{invoice.invoice_no}</div>
                  <div className="text-stone-500 text-xs">{invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString() : ""}</div>
                </div>
              </div>
              <div className="border border-stone-200 rounded-xl overflow-hidden">
                <div className="flex justify-between px-4 py-2.5 border-b border-stone-100">
                  <span className="text-stone-600">{invoice.plan_name} (monthly)</span>
                  <span className="font-mono">{fmt(invoice.symbol, invoice.base)}</span>
                </div>
                {invoice.gst_applicable && (
                  <div className="flex justify-between px-4 py-2.5 border-b border-stone-100">
                    <span className="text-stone-600">GST ({invoice.gst_percent}%)</span>
                    <span className="font-mono">{fmt(invoice.symbol, invoice.gst)}</span>
                  </div>
                )}
                <div className="flex justify-between px-4 py-2.5 bg-stone-50 font-medium">
                  <span>Total ({invoice.currency})</span>
                  <span className="font-mono">{fmt(invoice.symbol, invoice.total)}</span>
                </div>
              </div>
              <div className="text-xs text-stone-400">Payment ID: {invoice.razorpay_payment_id}</div>
              {!invoice.gst_applicable && <div className="text-xs text-stone-400">International invoice — no GST applied.</div>}
              <div className="flex gap-2">
                <Button className="rounded-full flex-1 bg-[#C2410C] hover:bg-[#9A3412] gap-1.5"
                  data-testid="invoice-dialog-download"
                  onClick={() => downloadInvoice({ id: invoice._pid, invoice_no: invoice.invoice_no })}>
                  <DownloadSimple size={15} /> Download PDF
                </Button>
                <Button className="rounded-full" variant="outline" onClick={() => window.print()}>Print</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
